/**
 * force-alt-text.js
 * Shopify port of the WordPress "Force Alt Text on All Images" plugin.
 *
 * Why this is JavaScript and not a server filter:
 *   The WordPress plugin buffered the whole page (ob_start on template_redirect)
 *   and rewrote every <img> tag in PHP. Shopify is fully hosted — there is no
 *   server-side output buffer or PHP hook we can attach to. The equivalent on
 *   Shopify is two layers:
 *     1. Liquid image helpers (the {{ image | image_tag }} filter) already emit
 *        alt="{{ image.alt }}" straight from the media library wherever the
 *        theme renders an image properly. That covers the common case.
 *     2. This script backfills everything the first layer misses — raw <img>
 *        tags, app/section-injected images, and media-library entries that have
 *        no alt set in the admin — deriving readable text from the best
 *        available context (mirrors the WP fallback to the attachment title).
 *
 * It also re-runs for images added after first paint (lazy-load, instant page
 * navigation, AJAX section renders) via a MutationObserver — the equivalent of
 * the WP filter firing on every render.
 *
 * Note: the most SEO-reliable alt text is still the value set in the Shopify
 * admin media library (server-rendered by layer 1). This script is the safety
 * net so no image ships to crawlers with a missing or empty alt attribute.
 */
(function () {
  'use strict';

  var MAX_ALT_LENGTH = 125; // keep alt text within a sensible length

  // Derive a human-readable name from an image URL's filename.
  // Handles Shopify CDN URLs, size suffixes, query params, and separators.
  function altFromSrc(src) {
    if (!src) return '';

    var path;
    try {
      path = new URL(src, window.location.href).pathname;
    } catch (e) {
      path = String(src).split('?')[0];
    }

    var file = path.split('/').pop() || '';
    try {
      file = decodeURIComponent(file);
    } catch (e) {
      /* leave as-is if it can't be decoded */
    }

    // Drop file extension (.jpg, .png, .webp, ...)
    file = file.replace(/\.[a-z0-9]+$/i, '');
    // Drop Shopify/legacy size suffixes: _1024x1024, _100x, _x200, -1024x768
    file = file.replace(/[_-]\d*x\d*$/i, '');
    // Separators -> spaces, collapse whitespace
    file = file.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!file) return '';

    // Title-case the words so the result reads naturally.
    file = file.replace(/\b\w/g, function (c) {
      return c.toUpperCase();
    });
    return file;
  }

  // Look for descriptive text near the image (caption, link, card heading).
  function nearestText(img) {
    var figure = img.closest('figure');
    if (figure) {
      var caption = figure.querySelector('figcaption');
      if (caption && caption.textContent.trim()) {
        return caption.textContent.trim().replace(/\s+/g, ' ');
      }
    }

    var link = img.closest('a');
    if (link) {
      var label = (link.getAttribute('aria-label') || link.getAttribute('title') || '').trim();
      if (label) return label;
    }

    // Product / collection card title, if the image lives inside one.
    var card = img.closest(
      '.card, .product-card, [class*="product-card"], [class*="card"], li, .grid__item'
    );
    if (card) {
      var heading = card.querySelector(
        '.card__heading, .product-card__title, h1, h2, h3, h4, [class*="title"], [class*="heading"]'
      );
      if (heading && heading.textContent.trim()) {
        return heading.textContent.trim().replace(/\s+/g, ' ');
      }
    }

    return '';
  }

  // Best alt text we can produce for this image, in priority order.
  function deriveAlt(img) {
    var hint = (
      img.getAttribute('data-alt') ||
      img.getAttribute('aria-label') ||
      img.getAttribute('title') ||
      ''
    ).trim();
    if (hint) return hint;

    var context = nearestText(img);
    if (context) return context;

    return altFromSrc(img.currentSrc || img.getAttribute('src'));
  }

  function shouldSkip(img) {
    // Explicit opt-out hook for templates that want to manage alt themselves.
    if (img.hasAttribute('data-skip-alt')) return true;
    // Intentionally decorative images should keep an empty alt for screen
    // readers — adding text here would hurt accessibility, not help SEO.
    if (img.getAttribute('role') === 'presentation') return true;
    if (img.getAttribute('aria-hidden') === 'true') return true;

    var existing = img.getAttribute('alt');
    // Already has meaningful alt (e.g. from the media library) — leave it.
    if (existing !== null && existing.trim() !== '') return true;

    return false;
  }

  function fixImage(img) {
    if (!img || img.tagName !== 'IMG') return;
    if (shouldSkip(img)) return;

    var alt = deriveAlt(img);
    if (!alt) return;

    if (alt.length > MAX_ALT_LENGTH) {
      alt = alt.slice(0, MAX_ALT_LENGTH - 1).trim() + '…';
    }
    img.setAttribute('alt', alt);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var images = scope.querySelectorAll('img');
    for (var i = 0; i < images.length; i++) {
      fixImage(images[i]);
    }
  }

  function init() {
    scan(document);

    if (!('MutationObserver' in window)) return;

    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var mutation = mutations[m];

        if (mutation.type === 'attributes') {
          fixImage(mutation.target);
          continue;
        }

        var added = mutation.addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            fixImage(node);
          } else if (node.querySelectorAll) {
            scan(node);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'alt']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
